import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import * as S from './PresetPhotoPicker.styles';
import { Drawer } from '@/components/Drawer/Drawer';
import { Button } from '@/components/UI/Button/Button';
import { CloseIcon } from '@/components/UI/Icons/Icons';
import { Popup } from '@/components/UI/Popup/Popup';
import { PresetPhotoPickerGrid } from '@/components/PresetPhotoPicker/PresetPhotoPickerGrid';
import { Typography } from '@/components/UI/Typography/Typography.styles';
import { AdImage } from '@/components/AdImages/AdImages.types';
import { PresetPhoto } from '@/api/types/categories.types';
import useScreenSize from '@/hooks/useScreenSize';
import { CommonTestIds } from '@/__e2e__/testIds/common';

interface PresetPhotoPickerProps {
  isOpen: boolean;
  close: () => void;
  onApply: (images: AdImage[]) => void;
  presetImages: PresetPhoto[];
  selectedImages: AdImage[];
}

export const PresetPhotoPicker: React.FC<PresetPhotoPickerProps> = ({
  isOpen,
  close,
  onApply,
  presetImages,
  selectedImages,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { isLarge } = useScreenSize();
  const [currentSelection, setCurrentSelection] = useState<AdImage[]>([]);
  const images: AdImage[] = presetImages.map(({ id, url }) => ({
    id,
    src: url,
  }));

  useEffect(() => {
    if (isOpen) {
      setCurrentSelection(selectedImages);
    }
  }, [isOpen]);

  function apply() {
    onApply(currentSelection);
    close();
  }

  function toggle(image: AdImage) {
    setCurrentSelection(prev => {
      const selectedImage = prev.find(({ id }) => id === image.id);

      if (selectedImage) {
        return prev.filter(({ id }) => id !== image.id);
      }

      return [...prev, image];
    });
  }

  if (isLarge) {
    return (
      <Popup
        isOpen={isOpen}
        close={close}
        customSize={{ width: 700 }}
        variant="2"
      >
        <S.PresetPhotoPickerPopupContainer>
          <S.PresetPhotoPickerPopupHeader>
            <Typography.Title $variant="4">
              {t('pages.new_ad.choose_image_from_gallery')}
            </Typography.Title>
            <Button appearance="transparent" hasPadding={false} onClick={close}>
              <CloseIcon width={20} height={20} />
            </Button>
          </S.PresetPhotoPickerPopupHeader>
          <PresetPhotoPickerGrid
            images={images}
            currentSelection={currentSelection}
            toggle={toggle}
            maxHeight={325}
          />
          <S.PresetPhotoPickerPopupFooter>
            <Typography.Caption $variant="2">
              {t('pages.new_ad.images_will_be_shown_in_search')}
            </Typography.Caption>
            <Button
              appearance="primary"
              isFullWidth
              borderRadius={14}
              onClick={apply}
              data-testid={CommonTestIds.adImages.presetPhotoPickerApplyButton}
            >
              {t('common.apply')}
            </Button>
          </S.PresetPhotoPickerPopupFooter>
        </S.PresetPhotoPickerPopupContainer>
      </Popup>
    );
  }

  return (
    <Drawer
      isAdaptive
      isOpen={isOpen}
      left={{
        icon: (
          <CloseIcon
            width={15}
            height={15}
            fill={theme.palette.primary.labels}
          />
        ),
        onClick: close,
      }}
      title={t('pages.new_ad.choose_image_from_gallery')}
      subtitle={
        <S.PresetPhotoPickerSubtitle>
          {t('pages.new_ad.images_will_be_shown_in_search')}
        </S.PresetPhotoPickerSubtitle>
      }
      background={theme.palette.primary.background}
      hasBottomBorder={false}
      hasBottomMargin={false}
    >
      <PresetPhotoPickerGrid
        images={images}
        currentSelection={currentSelection}
        toggle={toggle}
        hasPadding
      />

      <S.PresetPhotoPickerApply>
        <Button
          appearance="primary"
          isFullWidth
          borderRadius={14}
          onClick={apply}
          data-testid={CommonTestIds.adImages.presetPhotoPickerApplyButton}
        >
          {t('common.apply')}
        </Button>
      </S.PresetPhotoPickerApply>
    </Drawer>
  );
};

PresetPhotoPicker.displayName = 'PresetPhotoPicker';
